import { ProctoringOption, ProctoringPlatforms } from "@verity/proctoring-option/types";
import {
	getEditQuizStepName,
	PROCTORING_OPTION_TEXT,
	ProctoringOptionCategory,
} from "../proctoring-options/option-text/ProctoringOptionText";
import { useMobileLayout } from "../../hooks";
import { CoreModal } from "../../core";
import { Divider, Typography } from "@mui/material";
import OneDot from "../images/one-dot.svg?react";
import styles from "./UnsupportedModal.module.css";
import i18nPackage from "../../i18n/i18n";
import { useEditQuizStyles } from "../components";
import { ResponsiveModalBody } from "../components/EditQuizModalComponents";

type UnsupportedModalProps = {
	open: boolean;
	onClose: () => void;
	onConfirm: () => void;
	platformUnsupportedOptionNames: {
		[P in keyof ProctoringPlatforms | "none" | "passwordConflict"]?: {
			[P in ProctoringOptionCategory]?: Array<{
				name: string;
				option: keyof ProctoringOption;
			}>;
		};
	};
	onEnableAnyway?: () => void;
	enableAnyway?: boolean;
};

export default function UnsupportedModal({
	open,
	onClose,
	onConfirm,
	platformUnsupportedOptionNames,
	onEnableAnyway,
	enableAnyway = false,
}: UnsupportedModalProps) {
	const isMobileLayout = useMobileLayout();
	const { getModalInModalProps } = useEditQuizStyles();

	const iconProps = {
		style: { color: "#42296e" },
		width: "24px",
		height: "24px",
	};

	const getUnsupportedModalDescriptionText = () => {
		const platforms = Object.keys(platformUnsupportedOptionNames) as Array<keyof typeof platformUnsupportedOptionNames>;
		if (platforms.length === 1) {
			if (platforms[0] === "isExtension") {
				return i18nPackage.t("editQuizPage.unsupportedModalTSX.extensionDesc", {
					platform: PROCTORING_OPTION_TEXT[platforms[0]].header,
				});
			} else if (platforms[0] !== "none" && platforms[0] !== "passwordConflict") {
				return i18nPackage.t("editQuizPage.unsupportedModalTSX.otherPlatform", {
					platform: PROCTORING_OPTION_TEXT[platforms[0]].header,
				});
			} else if (platforms[0] === "passwordConflict") {
				return i18nPackage.t("editQuizPage.unsupportedModalTSX.passwordConflict");
			} else {
				return i18nPackage.t("editQuizPage.unsupportedModalTSX.defaultDescription");
			}
		} else if (platforms.length > 1) {
			return i18nPackage.t("editQuizPage.unsupportedModalTSX.multiPlatform");
		}
	};

	const title = i18nPackage.t("editQuizPage.unsupportedModalTSX.title");

	return (
		<CoreModal
			{...getModalInModalProps(isMobileLayout, title)}
			open={open}
			widthFixed
			minWidth={!isMobileLayout ? "min(760px, 80%)" : undefined}
			maxWidth={isMobileLayout ? "95%" : undefined}
			maxHeight="min(640px, 80%)"
			onClose={onClose}
			buttons={[
				{
					text: enableAnyway
						? i18nPackage.t("editQuizPage.unsupportedModalTSX.enableAnyway")
						: i18nPackage.t("editQuizPage.unsupportedModalTSX.cancel"),
					color: "secondary",
					onClick: enableAnyway ? onEnableAnyway : onClose,
				},
				{ text: i18nPackage.t("editQuizPage.unsupportedModalTSX.continue"), onClick: onConfirm },
			]}
		>
			<ResponsiveModalBody description={getUnsupportedModalDescriptionText()} title={title}>
				{Object.keys(platformUnsupportedOptionNames).map((p, i) => {
					const platform = p as keyof typeof platformUnsupportedOptionNames;
					const optionCategories = platformUnsupportedOptionNames[platform] ?? {};
					const multiplePlatforms = Object.keys(platformUnsupportedOptionNames).length > 1;
					return (
						<div key={i} className={styles.unsupportedContainer}>
							{multiplePlatforms && Object.keys(optionCategories).length > 0 && (
								<Typography className={styles.multiplePlatforms}>
									{platform === "none"
										? i18nPackage.t("editQuizPage.unsupportedModalTSX.defaultPlatform")
										: PROCTORING_OPTION_TEXT.isExtension.header}
									:
								</Typography>
							)}
							{Object.keys(optionCategories).map((c, j) => {
								const category = c as ProctoringOptionCategory;
								return (
									<div
										key={j}
										style={multiplePlatforms ? { padding: "0 0.75rem" } : undefined}
										className={styles.categoryContainer}
									>
										<Typography variant="h2" className={styles.category} style={{ marginTop: j > 0 ? "2rem" : "1rem" }}>
											{getEditQuizStepName(category)}
										</Typography>
										<Divider />

										{optionCategories[category]?.map((option, k) => (
											<div key={k} className={styles.optionRow}>
												<div className={styles.optionTitleIconWrapper}>
													<div style={{ width: "24px", height: "24px" }} aria-hidden="true">
														{PROCTORING_OPTION_TEXT[option.option].icon?.(iconProps) ?? <OneDot {...iconProps} />}
													</div>
													<Typography className={styles.optionName}>{option.name}</Typography>
												</div>
												<Typography className={styles.optionDescription}>
													{PROCTORING_OPTION_TEXT[option.option].description}
												</Typography>
											</div>
										))}
									</div>
								);
							})}
						</div>
					);
				})}
			</ResponsiveModalBody>
		</CoreModal>
	);
}
